import { Tab } from "@headlessui/react";
import cx from "classnames";

import { useTranslation } from "@/core/i18n";
import type { Models } from "api/types";
import { ImageCarousel } from "atoms/ImageCarousel/ImageCarousel";
import { pdpFrameClassName } from "atoms/ImageCarousel/PdpImageCarousel";
import { Skeleton } from "atoms/skeleton/Skeleton";

import { BeforeAfterGallery } from "./PackagePDP/before-after-gallery/BeforeAfterGallery";

type PdpGalleryProps = {
  slides: { src: string; alt: string }[];
  productName: string;
  beforeAfters: Models<"DisplayPatientTreatmentBeforeAfterPairResponse">[];
};

/** Holds the photo frame's exact place while a detail page loads. */
export const PdpGallerySkeleton = () => (
  <Skeleton className={cx(pdpFrameClassName, "rounded-none bg-gray-200")} />
);

export const PdpGallery = ({ slides, productName, beforeAfters }: PdpGalleryProps) => {
  const { t } = useTranslation();
  const hasImages = slides.length > 0;
  const hasResults = beforeAfters.length > 0;

  if (!hasImages && !hasResults) return null;

  const hasTabs = hasImages && hasResults;
  // The Photos / Before & After switcher (88px) shares the viewport with the frame.
  const resultsFrameClassName = hasTabs
    ? "desk:!h-[calc(100dvh-65px-88px)] desklg:!h-[calc(100dvh-73px-88px)] desk3xl:!h-auto desk3xl:!max-h-[calc(100dvh-73px-2.5rem-88px)]"
    : undefined;
  // The photo card hugs the photo; this keeps a tall one inside the room under the header, the
  // tab switcher and the card's own padding (8rem).
  const imageClassName = hasTabs
    ? "desk:max-h-[calc(100dvh-65px-88px-8rem)] desklg:max-h-[calc(100dvh-73px-88px-8rem)]"
    : "desk:max-h-[calc(100dvh-65px-8rem)] desklg:max-h-[calc(100dvh-73px-8rem)]";
  const images = (
    <ImageCarousel
      slides={slides}
      productName={productName}
      presentation="pdp"
      imageClassName={imageClassName}
    />
  );
  const results = (
    <div
      className={cx(
        "flex items-center overflow-hidden bg-background-two [&>div]:w-full",
        pdpFrameClassName,
        resultsFrameClassName,
      )}
    >
      <BeforeAfterGallery pairs={beforeAfters} presentation="pdp" />
    </div>
  );

  if (!hasImages) return results;
  if (!hasResults) return images;

  return (
    <Tab.Group as="div">
      <Tab.Panels>
        <Tab.Panel>{images}</Tab.Panel>
        <Tab.Panel>{results}</Tab.Panel>
      </Tab.Panels>
      <Tab.List className="grid h-[88px] grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-2 border-b border-outline-grey bg-white p-3 desk:px-6 desk3xl:border-b-0 desk3xl:px-0">
        <Tab
          className={({ selected }) =>
            cx(
              "flex items-center justify-center rounded-xl border px-3 text-body2 font-medium outline-offset-2 transition-colors",
              selected
                ? "border-primary bg-background-two text-primary"
                : "border-outline-grey text-secondary hover:bg-background-two hover:text-primary",
            )
          }
        >
          {t("imageCarousel.photos")}
        </Tab>
        <Tab
          className={({ selected }) =>
            cx(
              "flex items-center justify-center gap-3 rounded-xl border px-2 text-body2 font-medium outline-offset-2 transition-colors desk:gap-4",
              selected
                ? "border-primary bg-background-two text-primary"
                : "border-outline-grey text-primary hover:bg-background-two",
            )
          }
        >
          <span className="flex h-12 w-20 shrink-0 gap-0.5 overflow-hidden rounded-md bg-background-two">
            <img
              src={beforeAfters[0].beforeUrl}
              alt=""
              className="h-full min-w-0 flex-1 object-contain"
            />
            <img
              src={beforeAfters[0].afterUrl}
              alt=""
              className="h-full min-w-0 flex-1 object-contain"
            />
          </span>
          <span>{t("beforeAfterGallery.title")}</span>
        </Tab>
      </Tab.List>
    </Tab.Group>
  );
};
